import { useState, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiError, AuthNotReadyError } from '@/api/client';
import { AuthProvider } from '@/auth/AuthProvider';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ThemeProvider } from '@/hooks/useTheme';

export function AppProviders({ children }: { children: ReactNode }) {
  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            refetchOnWindowFocus: false,
            retry: (failures, error) => {
              // Boot race: the request was never sent because MSAL had not produced a token yet.
              // Retry briefly — the token usually lands within a few hundred milliseconds, and
              // AuthProvider also invalidates everything once it does.
              if (error instanceof AuthNotReadyError) return failures < 5;
              if (error instanceof ApiError && (error.isAuth || error.isForbidden || error.isMissing)) return false;
              return failures < 2;
            },
            retryDelay: (attempt) => Math.min(250 * 2 ** attempt, 2_000),
          },
        },
      }),
  );

  return (
    <ThemeProvider>
      <QueryClientProvider client={client}>
        <AuthProvider>
          <TooltipProvider>
            {children}
            <Toaster />
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
